import { FC, useMemo } from 'react';

import { User } from '@ravago/shared/authentication';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { RelatedDocument } from '@ravago/shared/page-data/models/elements';
import { Accordion } from '@ravago/shared/radiance/components/accordion/components/Accordion/Accordion';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { DownloadOption } from '@ravago/shared/radiance/components/input-field/components/Download/Download';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Sublevel } from '@ravago/shared/radiance/components/sublevel/components/Sublevel/Sublevel';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { firstBy } from 'thenby';

import { useDocumentShare } from '../../../../../hooks/use-document-share';
import { getLanguageCode, getLanguageNameFromLanguageCode } from '../../../../../utils/locale/locale.utils';
import { availableLanguageCodes } from '../../../quick-tds-link/components/quick-tds-link';
import DocumentListItem from '../related-document-list-item/document-list-item';

interface Props {
   documents: Array<RelatedDocument>;
   user?: User;
   hasSharePermission?: boolean;
   accordionTitle?: string;
   detailPageName?: string;
   ulProspectorId: string | undefined;
   publicShowTdsUom: boolean;
   showTdsUom: boolean;
   dismissable?: boolean;
   channelUOMs: Array<NormalizedUnitOfMeasure>;
   onDocumentDownload: (document: RelatedDocument, selectedLanguageId?: string, selectedUOMId?: string) => void;
   onViewDocument: (document: RelatedDocument, selectedLanguageId?: string, selectedUOMId?: string) => void;
   onDismiss?: () => void;
   onToggle?: () => void;
   onShareChecked: (document: RelatedDocument) => void;
   onLanguageChange?: (lan: DownloadOption | undefined) => void;
   onUOMChange?: (uom: DownloadOption | undefined) => void;
   selectedLanguage?: DownloadOption;
   selectedUOM?: DownloadOption;
   accordionOpen?: boolean;
   locale: string;
   sharedWithYou?: boolean;
   canDownloadDocumentsAnonymously?: boolean;
   canDownloadDocument: (document: RelatedDocument) => boolean;
   superCondensedView: boolean;
   condensedView: boolean;
}

export const RelatedDocumentCategory: FC<Props> = ({
   documents,
   user,
   accordionOpen,
   dismissable,
   hasSharePermission,
   publicShowTdsUom,
   selectedLanguage,
   selectedUOM,
   onLanguageChange,
   onUOMChange,
   onDismiss,
   showTdsUom,
   ulProspectorId,
   accordionTitle,
   onDocumentDownload,
   onViewDocument,
   onToggle,
   onShareChecked,
   detailPageName,
   locale,
   channelUOMs,
   sharedWithYou = false,
   canDownloadDocumentsAnonymously,
   canDownloadDocument,
   superCondensedView,
   condensedView
}) => {
   const translate = useTranslations();
   const language = getLanguageCode(locale);
   const availableLanguages = availableLanguageCodes.map((lang) => ({
      id: lang,
      value: getLanguageNameFromLanguageCode(lang, locale)
   }));
   const { setDownloadList } = useDocumentShare();

   const handleToggle = () => {
      onToggle?.();
   };

   const handleDownload = (document: RelatedDocument, selectedLanguageId?: string, selectedUOMId?: string) => {
      onDocumentDownload?.(document, selectedLanguageId, selectedUOMId);
   };

   const handleDownloadAllSharedDocuments = () => {
      if (sharedWithYou) {
         setDownloadList(documents);
      }
   };

   const handleDismissSharedDocuments = () => {
      localStorage.removeItem('sharedDocuments');
      onDismiss?.();
   };

   const sortedDocuments = useMemo(
      () =>
         documents?.sort(
            firstBy((d: RelatedDocument) => !d.isBrandedTds, { ignoreCase: true })
               .thenBy((d: RelatedDocument) => !d.isStandardTDS, { ignoreCase: true })
               .thenBy((d: RelatedDocument) => d.type, { ignoreCase: true })
               .thenBy((d: RelatedDocument) => d.description, { ignoreCase: true })
               .thenBy((d: RelatedDocument) => d.rank, { ignoreCase: true })
         ),
      [documents]
   );

   if (dismissable || documents.length === 0) return null;

   const documentPapers = (sharedDocument: boolean) =>
      sortedDocuments?.map((document) => (
         <Paper key={document.id}>
            <DocumentListItem
               user={user}
               language={language}
               ulProspectorId={ulProspectorId}
               sharedDocument={sharedDocument}
               hasSharePermission={hasSharePermission}
               onShareChecked={(doc) => onShareChecked(doc)}
               onLanguageChange={onLanguageChange}
               onUOMChange={onUOMChange}
               selectedLanguage={selectedLanguage}
               selectedUOM={selectedUOM}
               channelUOMs={channelUOMs}
               detailPageName={detailPageName}
               publicShowTdsUom={publicShowTdsUom}
               showTdsUom={showTdsUom}
               onDocumentDownload={(doc, selectedLanguageId, selectedUOMId) => handleDownload(doc, selectedLanguageId, selectedUOMId)}
               onViewDocument={(doc, selectedLanguageId, selectedUOMId) => onViewDocument(doc, selectedLanguageId, selectedUOMId)}
               locale={locale}
               availableLanguages={availableLanguages}
               document={document}
               canDownloadDocumentsAnonymously={canDownloadDocumentsAnonymously}
               canDownloadDocument={canDownloadDocument(document)}
               superCondensedView={superCondensedView}
               condensedView={condensedView}
            />
         </Paper>
      ));

   return (
      <>
         {!sharedWithYou && (
            <Accordion
               key={accordionTitle}
               open={accordionOpen}
               header={
                  <Box justify="between" items="center">
                     <Typography variant="body-highlight" color="strong">
                        {accordionTitle}
                     </Typography>
                     <Icon icon={accordionOpen ? 'chevron-up' : 'chevron-down'} />
                  </Box>
               }
               body={documentPapers(sharedWithYou)}
               onHeaderClick={() => handleToggle()}
            />
         )}
         {sharedWithYou && (
            <Sublevel>
               <Box direction="column">
                  <Box direction="row" justify="between">
                     <Typography variant="heading-3" component="h3">
                        {translate('related-documents.shared-with-you')}
                     </Typography>
                     <Box direction="row">
                        <Button variant="secondary" onClick={handleDismissSharedDocuments}>
                           {translate('related-documents.dismiss')}
                        </Button>
                        <Button variant="primary" leadingIcon="download" onClick={handleDownloadAllSharedDocuments}>
                           {translate('related-documents.download-all')}
                        </Button>
                     </Box>
                  </Box>
                  <Box direction="column" gap="xs">
                     {documentPapers(sharedWithYou)}
                  </Box>
               </Box>
            </Sublevel>
         )}
      </>
   );
};
